<!--
  Form Entries Component Template
  ===============================
  
  Comprehensive template for viewing and managing form entries with advanced filtering
  and responsive design. This template provides a complete interface for administrators
  to browse, filter, and manage form submissions.
  
  Key Features:
  - Dual filter modes: Form Type and Submitter filtering
  - Dynamic layout switching between table and card views
  - Advanced filtering with field-specific operations
  - Responsive design with mobile-optimized controls
  - Pagination controls for large datasets
  - Real-time filtering and sorting capabilities
  
  Layout Structure:
  1. Filter and Selection Controls - Mode switching and form type/submitter selection
  2. Action Buttons - Filter modal and new entry creation
  3. Main Content Area - Dynamic table or mobile cards based on screen size
  4. Pagination Controls - Navigation through result pages
  
  Responsive Behavior:
  - Desktop: Full table layout with advanced filtering modal
  - Mobile: Card-based layout with action sheet navigation
  - Adaptive: Automatic switching based on screen size detection
  
  @author MacSafety Development Team
  @version 2.0
-->

<!-- Main container with consistent spacing and layout -->
<div class="form-entries-container">
  <!-- ========================================
       FILTER AND SELECTION CONTROLS
       ======================================== -->
  
  <ion-card class="no-card-style ion-no-padding">
    <ion-grid class="ion-no-padding">
      <ion-row class="ion-no-padding ion-align-items-center">
        <!-- Left side: flexible, can shrink -->
        <ion-col class="ion-no-padding" style="min-width: 0;">
          <ion-item lines="none" class="ion-no-padding">
            <!-- Vertical dots menu button -->
            <ion-button
              fill="clear"
              slot="start"
              (click)="presentFilterModeActionSheet()"
              class="ion-no-padding ion-no-margin"
              size="small"
            >
              <ion-icon name="ellipsis-vertical" slot="icon-only"></ion-icon>
            </ion-button>
        
            <!-- Dynamic label -->
            <ion-label class="label-auto-width"
            slot="start"
            >
              <strong>{{ filterMode() === 'formType' ? 'Form Type:' : 'Submitter:' }}</strong>
            </ion-label>
        
            <!-- Form Type Select - now fills available space -->
            <ion-select 
              *ngIf="filterMode() === 'formType'"
              [placeholder]="'Select a form type'"
         [ngModel]="selectedFormType()" (ngModelChange)="onFormTypeChange($event)"
              interface="action-sheet"
              class="ion-text-truncate"
              [style]="{'max-width': isSmallScreen ? '200px' : 'auto'}"
              slot="start"
              
           
             >
              <ion-select-option *ngFor="let option of formTypeOptions" [value]="option.id">
                {{ option.title }}
              </ion-select-option>
            </ion-select>
        
            <!-- Submit Input - now fills available space -->
            <ion-input
              *ngIf="filterMode() === 'submitter'"
              [placeholder]="'Enter submitter name'"
              [ngModel]="selectedSubmitter()"
              (ngModelChange)="selectedSubmitter.set($event)"
              (keydown.enter)="onSubmitterSearch()"
              slot="start"
        
              class="ion-no-padding ion-no-margin"
              [style]="{'max-width': isSmallScreen ? '200px' : 'auto', 'border-bottom': '1px solid var(--ion-color-dark)'}"

              >
            </ion-input>
        
            <!-- Search button - keep at end -->
            <ion-button
              *ngIf="filterMode() === 'submitter'"
              fill="clear"
              color="primary"
              [disabled]="!selectedSubmitter() || selectedSubmitter().trim() === ''"
              (click)="onSubmitterSearch()"
              slot="start"
              [size]="isSmallScreen ? 'medium' : 'small'">
              <ion-icon name="search-outline" slot="icon-only" [size]="isSmallScreen ? 'large' : 'small'"></ion-icon>
            </ion-button>
          </ion-item>
        </ion-col>
    
        <!-- Right side: fixed width, won't shrink -->
        <ion-col size="auto" class="ion-no-padding">
          <ion-item lines="none" class="ion-no-padding">
            <!-- Filter button with active indicator -->
            <div class="filter-container">
              <ion-button
                *ngIf="(isSmallScreen ||(!isSmallScreen && filterMode() ==='formType'))"
                [fill]="isSmallScreen ? 'clear' : 'solid'"
                color="secondary"
                (click)="onOpenFilterModal()"
                [size]="isSmallScreen ? 'medium' : 'small'"
                [disabled]="
                  (filterMode() === 'submitter' && !selectedSubmitter()) ||
                  (filterMode() === 'formType' && !selectedFormType()) 
                "
                [class]="!isSmallScreen ? 'ion-padding-start' : ''"
                title="Filter entry results"
              >
                <ion-icon name="filter-outline" [slot]="isSmallScreen ? 'icon-only' : 'start'" [size]="isSmallScreen ? 'large' : 'small'"></ion-icon>
                <span *ngIf="!isSmallScreen" class="ion-hide-sm-down">Filter</span>
              </ion-button>
              
              <!-- Active filter chip overlay -->
              <ion-chip 
                *ngIf="hasActiveFilters()" 
                color="success" 
                size="small"
                class="active-filter-chip-overlay">
                <ion-icon name="checkmark-circle"></ion-icon>
              </ion-chip>
            </div>
    
            <!-- New Entry button -->
            <ion-button
              *ngIf="filterMode() === 'formType'"
              [fill]="isSmallScreen ? 'clear' : 'solid'"
              color="primary"
              (click)="onCreateNewForm()"
              [disabled]="!selectedFormType()"
              [size]="isSmallScreen ? 'medium' : 'small'"
              title="Create a new form entry"
              [class]="!isSmallScreen ? 'ion-padding-start' : ''"

            >
              <ion-icon name="add-circle-outline" [slot]="isSmallScreen ? 'icon-only' : 'start'" [size]="isSmallScreen ? 'large' : 'small'"
           ></ion-icon>
              <span *ngIf="!isSmallScreen" class="ion-hide-sm-down">New Entry</span>
            </ion-button>
          </ion-item>
        </ion-col>
      </ion-row>
    </ion-grid>
  
   
  
     
   
  </ion-card>


  <!-- ========================================
       MAIN CONTENT AREA
       ======================================== -->

  <div class="middle-section">
  


    <!-- Desktop Table Layout - Shown on larger screens when data is available -->
    <app-dynamic-table
      *ngIf="!shouldShowCardsLayout() && shouldShowEntries()"
      [loadingState]="loadingState()"
      [title]="'Form Entries'"
      [subtitle]="'View and manage form submissions'"
      [columns]="currentColumns()"
      [data]="formEntries()"
      [sortColumn]="sortState().column"
      [sortDirection]="sortState().direction"
      [formTypes]="formTypes"
      (sort)="onSort($event)"
      (action)="onViewEntry($event)"
      (filter)="onFilter($event)"
      (clearFilter)="onClearFilter()"
    >
    </app-dynamic-table>

    <!-- Mobile Cards Layout - Shown on smaller screens when data is available -->
    <app-mobile-cards
        *ngIf="shouldShowCardsLayout() && shouldShowEntries()"
        [loadingState]="loadingState()"
      [title]="'Form Entries'"
      [subtitle]="'View and manage form submissions'"
      [columns]="currentColumns()"
      [data]="formEntries()"
 
      [formTypes]="formTypes"
      (action)="onViewEntry($event)"

    
    >
    </app-mobile-cards>

    <!-- No Selection State - Shown when no form type or submitter is selected -->
    <ion-card *ngIf="!shouldShowEntries() && !loadingState().isLoading" class="state-card">
      <ion-card-content class="ion-text-center">
        <ion-icon
          name="document-outline"
          size="large"
          color="medium"
        ></ion-icon>
        <p color="medium">
          Please
          {{
            filterMode() === "formType"
              ? "select a form type"
              : "enter a submitter name"
          }}
          to view entries
        </p>
      </ion-card-content>
    </ion-card>
  </div>

  <!-- ========================================
       PAGINATION CONTROLS
       ======================================== -->

  <!-- Fixed Bottom Load More - Only shown when entries are available and loaded -->
  <ion-card
  class="no-card-style pagination-card ion-text-center"
  *ngIf="shouldShowEntries() && formEntries().length > 0"
>
  <ion-grid>
    <ion-row class="ion-align-items-center ion-no-padding ion-no-margin">
      <!-- Load More Button -->
      <ion-col size="12" >
        <ion-button
          [expand]="shouldShowCardsLayout() ? 'block' : undefined"
          fill="outline"
          (click)="onLoadMore()"
          [disabled]="loadingState().isLoading || !hasMoreEntries()"
        >
          <ion-spinner
            *ngIf="loadingState().isLoading"
            name="circular"
            slot="start"
            style="width: 16px; height: 16px;"
          ></ion-spinner>
          <ion-icon
            *ngIf="!loadingState().isLoading"
            name="download-outline"
            slot="start"
          ></ion-icon>
          {{ hasMoreEntries() ? 'Load More' : 'End of Entries' }}
        </ion-button>
      </ion-col>

      <!-- Current Entries Information -->
      <ion-col size="12" class="ion-text-center">
        <ion-label>
          <ion-text>
            <strong>{{ formEntries().length }} entries loaded</strong>
          </ion-text>
        </ion-label>
      </ion-col>
    </ion-row>
  </ion-grid>
</ion-card>

</div>

<!-- Action Sheet for Filter Mode Selection -->
<ion-action-sheet
  #filterActionSheet
  [header]="'Filter Mode'"
  [buttons]="actionSheetFilterModeButtons"
>
</ion-action-sheet>

<!-- Filter/Sort Modal - Advanced Filtering Interface -->
<ion-modal  
#filterSortModal
>
  <ng-template> <!-- Floating Close Button -->
    <ion-fab vertical="top" horizontal="end" slot="fixed">
      <ion-fab-button 
        size="small" 
        color="light" 
        (click)="onCloseFilterModal()"
        class="modal-close-fab"
      >
        <ion-icon name="close"></ion-icon>
      </ion-fab-button>
    </ion-fab>


<app-mobile-filter-modal
  [columns]="filterMode() === 'submitter' ? currentColumns() : advancedFilterColumns()"
[sortState]="sortState()"
[filterState]="filterState()"
  [formTypes]="formTypes"
  (sort)="onSort($event)"
  (filter)="onFilter($event)"
  (clearFilter)="onClearFilter()"
 
></app-mobile-filter-modal>
</ng-template>
</ion-modal>